export const processRawData = (rawData) => {
    if (!rawData || rawData.length === 0) {
        return {
            synthesisMethods: [],
            substanceTypes: [],
            stats: {
                totalProducts: 0,
                totalSubTypes: 0,
                topSubstanceType: 'N/A',
                topSubstanceTypeCount: 0,
                biologicallyCount: 0,
                chemicallyCount: 0,
                topSubTypes: []
            }
        };
    }

    // Process synthesis methods
    const synthesisCounts = rawData.reduce((acc, item) => {
        const method = item.synthesis_method || 'Unknown';
        if (!acc[method]) {
            acc[method] = { total: 0 };
        }
        acc[method].total += 1;

        const type = item.substance_type || 'Unknown';
        acc[method][type] = (acc[method][type] || 0) + 1;

        return acc;
    }, {});

    const synthesisMethods = Object.entries(synthesisCounts).map(([name, counts]) => ({
        name,
        ...counts
    }));

    // Process substance types
    const typeCounts = rawData.reduce((acc, item) => {
        const type = item.substance_type || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});

    const substanceTypes = Object.entries(typeCounts).map(([name, value]) => ({
        name,
        value,
        subTypes: {} // Initialize subTypes object
    }));

    // Process subtypes for each type and count total subtypes
    const allSubTypes = new Set();
    const allSubTypesList = [];

    rawData.forEach(item => {
        const type = item.substance_type || 'Unknown';
        const subType = item.substance_sub_type || 'Unknown';
        const synthesisMethod = item.synthesis_method || 'Unknown';

        const typeObj = substanceTypes.find(t => t.name === type);
        if (typeObj) {
            // Ensure subTypes object exists
             if (!typeObj.subTypes) {
                 typeObj.subTypes = {};
             }

            if (!typeObj.subTypes[subType]) {
                typeObj.subTypes[subType] = { count: 0, products: [] };
                 // Only add unique subtypes to the set and list
                 if (!allSubTypes.has(subType)) {
                    allSubTypes.add(subType);
                    allSubTypesList.push({ type, subType, count: 0 });
                }
            }
            typeObj.subTypes[subType].count += 1;

            // Update the count in our tracking list
            const subTypeObj = allSubTypesList.find(st => st.subType === subType); // Simpler find if subtypes are unique across types assumption holds, otherwise need st.type === type && st.subType === subType
             if (subTypeObj) {
                // Only increment if it matches the current item's type as well, if subtypes can exist under multiple types
                 if (subTypeObj.type === type) {
                    subTypeObj.count += 1;
                 } else if (!allSubTypesList.find(st => st.type === type && st.subType === subType)){
                    // If subtype exists under another type, add a new entry for this type/subtype combo
                     allSubTypesList.push({ type, subType, count: 1 });
                 }
             } else {
                 // This case might not be needed if initialized correctly above
                 allSubTypesList.push({ type, subType, count: 1 });
             }


            if (item.eu_pnumber) {
                typeObj.subTypes[subType].products.push({
                    pnumber: item.eu_pnumber,
                    name: item.eu_brand_name_current || item.active_substance_current || 'Unknown',
                    authDate: item.eu_aut_date,
                    substanceType: type,
                    substanceSubType: subType,
                    substanceSubSubType: item.substance_sub_sub_type,
                    synthesisMethod: synthesisMethod
                });
            }
        }
    });

    // Get top substance type
    const sortedTypes = [...substanceTypes].sort((a, b) => b.value - a.value);
    const topType = sortedTypes.length > 0 ? sortedTypes[0] : null;

    // Get top subtypes
    const sortedSubTypes = [...allSubTypesList].sort((a, b) => b.count - a.count).slice(0, 5);

    // Count by synthesis method
    const biologicallyCount = synthesisMethods.find(method => method.name === 'Biologically')?.total || 0;
    const chemicallyCount = synthesisMethods.find(method => method.name === 'Chemically')?.total || 0;

    return {
        synthesisMethods,
        substanceTypes, // This now includes the nested subTypes with products
        stats: {
            totalProducts: rawData.length,
            totalSubTypes: allSubTypes.size,
            topSubstanceType: topType ? topType.name : 'N/A',
            topSubstanceTypeCount: topType ? topType.value : 0,
            biologicallyCount,
            chemicallyCount,
            topSubTypes: sortedSubTypes
        }
    };
};


export const calculateDateStats = (products) => {
    if (!products || products.length === 0) {
        return null;
    }

    const validDates = products
        .map(p => p.authDate ? new Date(p.authDate) : null)
        .filter(d => d instanceof Date && !isNaN(d)); // Filter valid Date objects

    if (validDates.length === 0) {
        return null; // No valid dates found
    }

    const earliestAuth = new Date(Math.min(...validDates.map(d => d.getTime())));
    const latestAuth = new Date(Math.max(...validDates.map(d => d.getTime())));
    const rangeInDays = (latestAuth - earliestAuth) / (1000 * 60 * 60 * 24);
    const rangeInYears = (rangeInDays / 365.25); // Use 365.25 for average year length

    let rangeText = "Invalid date range"; // Default text

    if (rangeInDays < 0) {
        return null; // Invalid range if latest is before earliest
    } else if (rangeInDays < 1) {
        rangeText = "Same day";
    } else if (rangeInDays < 365.25) {
       rangeText = `${Math.round(rangeInDays)} days`;
    } else {
        rangeText = `${rangeInYears.toFixed(1)} years`;
    }

     return {
        earliest: earliestAuth.toLocaleDateString(),
        latest: latestAuth.toLocaleDateString(),
        rangeText: rangeText
    };
};